import {DataTestAttrs, getTestProps} from '@benchling/js-test-utils';
import {css, StyleDeclaration, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import HelpButton from 'help/HelpButton';
import {FONT_PROXIMA_11} from 'styles/FontConstants';
import {GRAY_DARKER, RED_DARK} from 'ui-library/Colors';
import {FloatingInteraction} from 'ui-library/floating-ui/FloatingConstants';

import FieldLabel from './FieldLabel';
import {InputErrorMessageStyle, InputProps, Size} from './InputConstants';

export interface InputWrapperProps extends InputProps {
  children: React.ReactNode;
  dataTestAttrs?: DataTestAttrs;
  id: string;
  helpInteraction?: FloatingInteraction.CLICK | FloatingInteraction.HOVER;
}

const styles = StyleSheet.create({
  errorMessage: {
    ...FONT_PROXIMA_11,
  },
  errorMessageInfo: {
    color: GRAY_DARKER,
  },
  errorMessageError: {
    color: RED_DARK,
  },
});

const ERROR_MESSAGE_STYLE: Record<InputErrorMessageStyle, StyleDeclaration> = {
  [InputErrorMessageStyle.INFO]: styles.errorMessageInfo,
  [InputErrorMessageStyle.ERROR]: styles.errorMessageError,
};

/**
 * Component for input of string fields, providing labels, icons, and validation.
 * Label styles are re-exported on this class.
 */
export default function InputWrapper(props: InputWrapperProps): JSX.Element {
  return (
    <div className={props.className} {...getTestProps(props.dataTestAttrs)}>
      <div
        className={classNames('fieldInput-label-container', {
          'sr-only': props.labelStyle === FieldLabel.STYLES.HIDDEN,
        })}
      >
        <FieldLabel
          htmlFor={props.id}
          isRequired={props.isRequired}
          label={props.label}
          labelStyle={props.labelStyle}
        />
        {props.help && (
          <HelpButton
            renderContent={() => props.help}
            interaction={props.helpInteraction}
            iconClassName="u-mini-spacing-left"
          />
        )}
      </div>
      {props.description && (
        <div className={classNames('fieldInput-description', props.descriptionClassName)}>
          {props.description}
        </div>
      )}
      <div className="u-flex-display">
        {props.children}
        {props.button && React.cloneElement(props.button, {className: 'button--inline'})}
      </div>
      {props.error && (
        <div className={classNames(css(styles.errorMessage), css(ERROR_MESSAGE_STYLE[props.error.style]))}>
          {props.error.message}
        </div>
      )}
    </div>
  );
}
InputWrapper.defaultProps = {
  isRequired: false,
  labelStyle: FieldLabel.STYLES.HIDDEN,
  size: Size.SMALL,
  helpInteraction: FloatingInteraction.CLICK,
};
